import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { GuaranteeDecision, PrescriptionLineView } from '@lib/guarantees/data-access';
import { setGuaranteeDecision } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { DECISION_ICON, DECISION_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { DECISION_ANSWERS, DECISION_BLOCKER_HINT_KEY, DecisionAnswer, decisionBlocker } from '@lib/guarantees/feature/components/common/prescription-decisions';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../components/common/callout.component';
import { ControlDirective } from '../components/common/control.directive';
import { FieldLabelComponent } from '../components/common/field-label.component';
import { OptionPickerComponent, PickerOption } from '../components/common/option-picker.component';
import { ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES, FIELD_MESSAGE_CLASS, SECTION_LABEL_CLASS } from '../components/ui/ui.classes';
import { CONFIRM_CLASS } from './confirm-dialog.component';
import { DECLINE_REASONS, GUARANTEE_REASONS } from './decision-reasons';

/**
 * The grounds each answer is recorded under — one list per answer, never shared. Granting and
 * refusing are different acts, and neither is the amendment vocabulary the edit and delete flows
 * use, so a wording offered under one of them must never be offered under another.
 */
const REASONS: Record<DecisionAnswer, string[]> = {
  [GuaranteeDecision.Guaranteed]: GUARANTEE_REASONS,
  [GuaranteeDecision.NotGuaranteed]: DECLINE_REASONS,
};

const REASON_LABEL_KEY: Record<DecisionAnswer, string> = {
  [GuaranteeDecision.Guaranteed]: 'guaranteeV2.label.guaranteeReason',
  [GuaranteeDecision.NotGuaranteed]: 'guaranteeV2.label.declineReason',
};

const REASON_PLACEHOLDER_KEY: Record<DecisionAnswer, string> = {
  [GuaranteeDecision.Guaranteed]: 'guaranteeV2.message.guaranteeReasonPlaceholder',
  [GuaranteeDecision.NotGuaranteed]: 'guaranteeV2.message.declineReasonPlaceholder',
};

export interface DecisionFormData {
  /** Names what the ruling was opened over, so the dialog is not headed "line" over a whole visit. */
  titleKey: string;
  /** Preselected answer, where the flow was entered from a control that already names one. */
  decision?: DecisionAnswer;
  /** Everything the ruling was opened on — a line, a course, or a whole visit's worth. */
  lines: PrescriptionLineView[];
}

export interface DecisionFormResult {
  decision: DecisionAnswer;
  /** The wording as a coding carrying no code: there is no dictionary of reasons yet. */
  reason: { name: string };
  note?: string;
  /** Exactly the lines the answer may be applied to — never all the ones it was opened on. */
  prescriptionIds: string[];
}

/** One line as the flow reports it: whether this answer applies to it, and why not when it doesn't. */
interface DecisionLineView {
  id: string;
  name: string;
  included: boolean;
  blockerKey: string;
}

/** Which record is being ruled on, and which of its lines the ruling was opened over. */
export interface DecisionFormContext {
  guaranteeId: string;
  lines: PrescriptionLineView[];
  /** Names the scope the ruling was opened at — a line, a course, or the whole visit. */
  titleKey: string;
  /** Set where the control that opened the flow already names an answer, as the triage buttons do. */
  decision?: DecisionAnswer;
}

/**
 * Opens the ruling and carries its answer into the store. Held beside the form rather than in each
 * caller: a line's menu, a course's menu, the visit header and the work list must all rule the same
 * way, and all four dispatch one write however many lines it names.
 */
export function openDecisionForm(dialog: MatDialog, store: Store, context: DecisionFormContext): void {
  const data: DecisionFormData = { titleKey: context.titleKey, decision: context.decision, lines: context.lines };

  dialog
    .open<DecisionFormComponent, DecisionFormData, DecisionFormResult>(DecisionFormComponent, {
      width: '90vw',
      maxWidth: '520px',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    })
    .afterClosed()
    .pipe(take(1))
    .subscribe(result => {
      if (!result) {
        return;
      }

      store.dispatch(
        setGuaranteeDecision({
          guaranteeId: context.guaranteeId,
          prescriptionIds: result.prescriptionIds,
          decision: result.decision,
          reason: result.reason,
          note: result.note,
        })
      );
    });
}

/**
 * One ruling with both answers on it, chosen against the lines it would apply to — which answer is
 * open depends on where each line stands, so the flow states line by line what it would leave
 * untouched rather than presenting a count that hides it.
 *
 * Both answers take grounds from a closed list: two managers ruling the same way in different words
 * look like two different rulings to anyone reporting on them afterwards.
 */
@Component({
  selector: 'guarantees-decision-form',
  standalone: true,
  imports: [FormsModule, MatIcon, MatTooltip, TranslatePipe, CalloutComponent, ControlDirective, FieldLabelComponent, OptionPickerComponent, ToggleDirective],
  templateUrl: './decision-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  /** Holds a line under the control it belongs to, so a message never reflows the form. */
  protected readonly fieldMessage = FIELD_MESSAGE_CLASS;

  private readonly dialogRef = inject(MatDialogRef<DecisionFormComponent, DecisionFormResult>);

  protected readonly data = inject<DecisionFormData>(MAT_DIALOG_DATA);

  /** Names the block of lines the ruling would act on, at the weight the panels label facts. */
  protected readonly sectionLabelClass = SECTION_LABEL_CLASS;

  protected readonly answers = DECISION_ANSWERS;
  protected readonly decisionIcon = DECISION_ICON;
  protected readonly decisionLabelKey = DECISION_LABEL_KEY;

  protected readonly decision = signal<DecisionAnswer>(this.data.decision ?? GuaranteeDecision.Guaranteed);
  protected readonly reason = signal('');
  protected readonly note = signal('');

  protected readonly isDecline = computed(() => this.decision() === GuaranteeDecision.NotGuaranteed);

  /** The confirm dialog's own tone classes: submitting a ruling is the same act at the same weight. */
  protected readonly submitClass = computed(() => CONFIRM_CLASS[this.isDecline() ? 'error' : 'success']);

  protected readonly reasonLabelKey = computed(() => REASON_LABEL_KEY[this.decision()]);
  protected readonly reasonPlaceholderKey = computed(() => REASON_PLACEHOLDER_KEY[this.decision()]);
  protected readonly reasonOptions = computed<PickerOption[]>(() => REASONS[this.decision()].map(value => ({ value })));

  /**
   * What the chosen answer would do to each line it was opened on. Deleted lines are left out
   * rather than listed as excluded: they are not part of the care plan, so they are not something
   * this ruling declined to act on — they are not what it is about at all.
   */
  protected readonly lines = computed<DecisionLineView[]>(() => {
    const decision = this.decision();

    return this.data.lines
      .filter(line => !line.prescription.isDeleted)
      .map(line => {
        const blocker = decisionBlocker(line, decision);

        return {
          id: line.prescription.id,
          name: line.prescription.name?.trim() || line.prescription.code,
          included: !blocker,
          blockerKey: blocker ? DECISION_BLOCKER_HINT_KEY[blocker] : '',
        };
      });
  });

  protected readonly included = computed(() => this.lines().filter(line => line.included));

  /** Either answer states its grounds: a ruling nobody gave a reason for cannot be reviewed. */
  protected readonly canSubmit = computed(() => this.included().length > 0 && !!this.reason().trim());

  protected setDecision(decision: DecisionAnswer): void {
    if (decision === this.decision()) {
      return;
    }

    this.decision.set(decision);
    // The grounds belong to the answer that was given, so switching answers clears them rather than
    // carrying a refusal's wording onto a grant.
    this.reason.set('');
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    const note = this.note().trim();

    this.dialogRef.close({
      decision: this.decision(),
      reason: { name: this.reason().trim() },
      note: note || undefined,
      prescriptionIds: this.included().map(line => line.id),
    });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
